import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useCart } from "../context/CartContext";
import { formatFCFA, whatsappLink } from "../lib/format";
import { Button } from "../components/ui/button";
import { CheckCircle2, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ProofUpload } from "../components/storefront/ProofUpload";

export default function OrderConfirmation() {
  const { numero } = useParams();
  const [params] = useSearchParams();
  const cle = params.get("cle") || "";
  const { settings } = useSettings();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    clear();
    api.get(`/orders/numero/${numero}`, { params: { cle } }).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero]);

  if (notFound) {
    return (
      <div className="py-32 text-center text-[#6E544A]" data-testid="confirmation-not-found">
        <p>Pour voir votre commande, utilisez le suivi avec votre numéro et votre téléphone.</p>
        <Link to={`/suivi?numero=${numero}`} className="mt-4 inline-block font-medium text-[#D76E4D] hover:underline">Suivre ma commande →</Link>
      </div>
    );
  }

  if (!order || !settings) return <div className="py-32 text-center text-[#9B7E73]">Chargement...</div>;

  const methods = settings.payment_methods || [];
  const method = methods.find((m) => m.id === order.paiement?.mode) || methods.find((m) => m.id === "wave") || { label: "Wave", numero: settings.wave_number, instructions: "" };
  const hasNumero = !!method.numero;
  const waMessage = hasNumero
    ? `Bonjour Le Dressing de Yoyo, voici la preuve de paiement (${method.label}) pour ma commande ${order.numero} d'un montant de ${formatFCFA(order.total)}.`
    : `Bonjour Le Dressing de Yoyo, je confirme ma commande ${order.numero} (${method.label}) d'un montant de ${formatFCFA(order.total)}.`;
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié !"); };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:py-16">
      <div className="text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-[#4A7C59]" />
        <h1 className="mt-4 font-serif text-3xl font-medium text-[#2C1A14] sm:text-4xl">Commande enregistrée !</h1>
        <p className="mt-2 text-[#6E544A]">Votre numéro de commande : <strong data-testid="order-numero">{order.numero}</strong></p>
      </div>

      <div className="mt-8 rounded-2xl border-2 border-[#D76E4D] bg-white p-6">
        <span className="rounded-full bg-[#F6E4DE] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#C05A3A]">Paiement : {method.label}</span>
        {hasNumero ? (
          <>
            <h2 className="mt-4 font-serif text-xl text-[#2C1A14]">Étape 1 — Payez par {method.label}</h2>
            <p className="mt-2 text-sm text-[#6E544A]">Effectuez un transfert de <strong className="text-[#D76E4D]">{formatFCFA(order.total)}</strong> au numéro suivant :</p>
            <div className="mt-3 flex items-center justify-between rounded-lg bg-[#F5E8E3] p-4">
              <div>
                <p className="text-lg font-bold text-[#2C1A14]" data-testid="wave-number">{method.numero}</p>
                <p className="text-xs text-[#9B7E73]">{settings.wave_name}</p>
              </div>
              <button onClick={() => copy(method.numero)} data-testid="copy-wave" className="rounded-full bg-white p-2 ring-1 ring-[#E8D5CE]"><Copy className="h-4 w-4 text-[#6E544A]" /></button>
            </div>
            {method.instructions && <p className="mt-3 text-sm leading-relaxed text-[#6E544A]">{method.instructions}</p>}
            <h2 className="mt-6 font-serif text-xl text-[#2C1A14]">Étape 2 — Envoyez la preuve</h2>
            <p className="mt-2 text-sm text-[#6E544A]">Envoyez-nous la capture de votre paiement directement ici :</p>
            <ProofUpload numero={order.numero} already={!!order.paiement?.preuve_recue} />
            <p className="mt-4 text-center text-xs text-[#9B7E73]">ou envoyez-la sur WhatsApp :</p>
          </>
        ) : (
          <>
            <h2 className="mt-4 font-serif text-xl text-[#2C1A14]">Paiement à la livraison</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#6E544A]">{method.instructions}</p>
            <p className="mt-3 text-sm text-[#6E544A]">Confirmez votre commande via WhatsApp pour organiser la livraison :</p>
          </>
        )}
        <a href={whatsappLink(settings.whatsapp_number, waMessage)} target="_blank" rel="noreferrer">
          <Button data-testid="whatsapp-proof-button" variant={hasNumero ? "outline" : "default"} className={`mt-2 w-full rounded-full py-6 text-base ${hasNumero ? "border-[#25D366] bg-white text-[#1da851] hover:bg-[#E8F8EE]" : "bg-[#25D366] text-white hover:bg-[#1da851]"}`}>
            <MessageCircle className="mr-2 h-5 w-5" /> {hasNumero ? "Envoyer la preuve sur WhatsApp" : "Confirmer sur WhatsApp"}
          </Button>
        </a>
      </div>

      <div className="mt-6 rounded-xl bg-white p-5 ring-1 ring-[#E8D5CE]">
        <h3 className="mb-3 font-serif text-lg text-[#2C1A14]">Détail de la commande</h3>
        {order.items.map((it, i) => (
          <div key={it.product_id || i} className="flex justify-between border-b border-[#F0E4DE] py-2 text-sm text-[#6E544A] last:border-0">
            <span>{it.nom} ({it.taille}) ×{it.quantite}</span>
            <span>{formatFCFA(it.prix_unitaire * it.quantite)}</span>
          </div>
        ))}
        <div className="mt-3 flex justify-between text-sm text-[#6E544A]"><span>Livraison ({order.client.commune})</span><span>{formatFCFA(order.frais_livraison)}</span></div>
        <div className="mt-2 flex justify-between text-lg font-semibold text-[#2C1A14]"><span>Total</span><span>{formatFCFA(order.total)}</span></div>
      </div>

      <div className="mt-8 flex flex-col items-center gap-3 text-center">
        <Link to={`/suivi?numero=${order.numero}`} data-testid="track-order-link" className="text-sm font-medium text-[#2C1A14] underline underline-offset-4 hover:text-[#D76E4D]">Suivre ma commande</Link>
        <Link to="/catalog" className="text-sm font-medium text-[#D76E4D] hover:underline">Continuer mes achats →</Link>
      </div>
    </div>
  );
}
